import type { AppearanceRow } from '@setplex/tria-api'
import type { JSX, ReactNode } from 'react'
import type { IContinueWatchingCardFormat } from '~/entities/continue-watching'
import type { MotnItem } from '~/entities/motn'
import type { PartnerProductFormatted } from '~/entities/partner-product'
import type { UiTvShowBundleInfo } from '~/entities/shows/bundles/index.h'
import type { UiTvBundleInfo } from '~/entities/tv/bundles/index.h'
import type {
  EventWithPrice,
  ShowWithPrice,
  TvWithPrice,
  UiVodBundleInfo,
  VodWithPrice,
} from '~/shared/interfaces'
import type { ViewComponent } from '../../model/components/index.h'

import { ROW_GAP } from "/app/libraries/arcana/src/vars/index.ts"
import { RowSimpleTemplate } from "/app/libraries/arcana/src/containers/RowContainer/Templates/index.ts"

import { useStoreMap, useUnit } from 'effector-react'
import { Fragment, memo, useCallback, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { $seeAllTitle } from '~/shared/ui/translations'
import { loadContent as modelLoadContent } from '../../model/components/model'
import {
  seeAllLiveEventsClick,
  seeAllPartnerProgramClick,
} from '../../model/rows/model'
import { $viewComponents } from '../../model/view-components'
import {
  CustomBundleCard,
  CWCard,
  EventCard,
  MotnCard,
  PartnerCard,
  TvChannelCard,
  TvShowCard,
  VodCard,
} from '../cards'
import { isVisibleCwRowItem } from '../cards/cw-card-visibility'
import { getRowItemKey } from '../lib/row-item-key'
import {
  getDataTestIdRow,
  getRowBackgroundGradient,
  getRowBackgroundStyle,
  getRowContentType,
  getRowPosterImage,
  getRowType,
} from '../lib/style'

type Props = { id: string }

export const Row = memo(Row_)

Row_.displayName = 'Row'
function Row_({ id }: Props): JSX.Element {
  const row = useStoreMap({
    store: $viewComponents,
    keys: [id],
    fn: (loaded, [id]) => loaded.find((row) => row.id === id),
  }) as ViewComponent<AppearanceRow> | undefined

  const { type: rowState } = row ?? {}

  const [
    loadContent,
    seeAllTitle,
    seeAllLiveEventsClickEvent,
    seeAllPartnerProgramClickEvent,
  ] = useUnit([
    modelLoadContent,
    $seeAllTitle,
    seeAllLiveEventsClick,
    seeAllPartnerProgramClick,
  ])

  const { ref, inView } = useInView()

  const isLoadNeeded = rowState === 'Empty' || rowState === 'Dirty'

  const isRowLoading =
    rowState === 'Empty' ||
    rowState === 'Dirty' ||
    rowState === 'Loading' ||
    rowState === 'Updating' ||
    rowState === 'Awaiting'

  useEffect(() => {
    if (row && inView && isLoadNeeded) {
      loadContent(row.appearance)
    }
  }, [inView, isLoadNeeded, loadContent, row])

  const getRowCardTemplate = useCallback(
    (item: any, idx: number) => {
      switch (row?.appearance.contentType) {
        case 'VOD':
          return <VodCard row={row} item={item as VodWithPrice} idx={idx} />
        case 'MOTN_VOD':
          return <MotnCard row={row} item={item as MotnItem} idx={idx} />
        case 'LIVE_EVENT':
          return <EventCard row={row} item={item as EventWithPrice} idx={idx} />
        case 'MIXED':
          return <CWCard row={row} item={item as IContinueWatchingCardFormat} />
        case 'PARTNER_PRODUCT':
          return (
            <PartnerCard row={row} item={item as PartnerProductFormatted} />
          )
        case 'TV_CHANNEL':
          return (
            <TvChannelCard row={row} item={item as TvWithPrice} idx={idx} />
          )
        case 'TV_SHOW':
          return <TvShowCard row={row} item={item as ShowWithPrice} idx={idx} />
        case 'MOTN_TV_SHOW':
          return <MotnCard row={row} item={item as MotnItem} idx={idx} />
        case 'VOD_BUNDLE':
        case 'TV_SHOW_BUNDLE':
        case 'TV_BUNDLE':
          return (
            <CustomBundleCard
              row={row}
              item={
                item as UiVodBundleInfo | UiTvBundleInfo | UiTvShowBundleInfo
              }
            />
          )
        default:
          return <></>
      }
    },
    [row]
  )

  const rowContentType = getRowContentType(row)
  const rowBackgroundGradient = getRowBackgroundGradient(row)
  const rowBackgroundStyle = getRowBackgroundStyle(row)
  const posterImage = getRowPosterImage(row)
  const withDescriptionRow =
    row?.appearance.contentType === 'LIVE_EVENT' ||
    row?.appearance.contentType === 'MIXED' ||
    row?.appearance.contentType === 'PARTNER_PRODUCT'

  const handleOnSeeAllClick = () => {
    const contentType = row?.appearance.contentType

    if (contentType === 'LIVE_EVENT' || contentType === 'PARTNER_PRODUCT') {
      const seeAllClickConfig = {
        PARTNER_PRODUCT: seeAllPartnerProgramClickEvent,
        LIVE_EVENT: seeAllLiveEventsClickEvent,
      }

      seeAllClickConfig[contentType]()
    }
  }

  if (!row) {
    return <></>
  }

  const {
    appearance: {
      description,
      title,
      style: { posterSize, topContent },
    },
    content,
    isMoreThanOnePage,
  } = row

  const type = getRowType(row)
  const gap = topContent ? ROW_GAP.SIZE_20 : ROW_GAP.SIZE_10
  const isMixedRow = row.appearance.contentType === 'MIXED'
  const visibleContent = isMixedRow
    ? content.filter(isVisibleCwRowItem)
    : content

  const RowTemplate = (children: ReactNode) => {
    const dataTestIdRow = getDataTestIdRow(row)

    return (
      <RowSimpleTemplate
        gap={gap}
        innerRef={ref}
        description={description}
        size={posterSize}
        type={type}
        title={title}
        contentType={rowContentType}
        itemCount={visibleContent.length}
        backgroundStyle={rowBackgroundStyle}
        backgroundGradient={rowBackgroundGradient}
        isMoreThanOnePage={isMoreThanOnePage}
        rowPosterImage={posterImage}
        onSeeAllClick={handleOnSeeAllClick}
        withCardDescription={withDescriptionRow}
        buttonTitle={seeAllTitle}
        isRowLoading={isRowLoading}
        dataTestIdRow={dataTestIdRow}
      >
        {children}
      </RowSimpleTemplate>
    )
  }

  if (isRowLoading) {
    return RowTemplate(<></>)
  }

  if (rowState === 'Failed' || !visibleContent || visibleContent.length === 0) {
    return <></>
  }

  return RowTemplate(
    <>
      {visibleContent.map((item: any, idx: number) => (
        <Fragment key={getRowItemKey(item, idx)}>
          {getRowCardTemplate(item, idx)}
        </Fragment>
      ))}
    </>
  )
}
